import { useEffect } from 'react'
import type { PenDrawingState } from './use-pen-drawing-state'

/**
 * Keyboard: Enter/Escape → end open path, Escape with <2 pts → discard; Ctrl/Cmd+Z pops the last
 * in-flight anchor; V completes the path before the left panel switches tools. Also tracks the
 * spacebar so pointer events pass through to the canvas pan handler while it's held.
 */
export function usePenKeyboard(drawing: PenDrawingState) {
  useEffect(() => {
    const { pointsRef, dragRef, forceUpdate, setIsNearFirst, spaceHeldRef } = drawing
    const { resetDrawing, completePath } = drawing // first-render closures; read refs only

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeldRef.current = true
      }

      const target = e.target as HTMLElement | null

      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      )
        return

      if (
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        !e.altKey &&
        (e.key === 'z' || e.key === 'Z')
      ) {
        const pts = pointsRef.current

        if (pts.length === 0) return // nothing in-flight — let global undo run normally
        e.preventDefault()
        e.stopImmediatePropagation()
        const newPts = pts.slice(0, -1)

        pointsRef.current = newPts
        dragRef.current = null
        if (newPts.length < 3) setIsNearFirst(false)
        forceUpdate((n) => n + 1)
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopImmediatePropagation()
        const pts = pointsRef.current

        if (pts.length >= 2) completePath(pts, pts.length > 2)
        return
      }

      if (e.key === 'v' || e.key === 'V') {
        // Complete any in-progress path, then let the event propagate so the
        // left-panel V handler switches to select mode.
        const pts = pointsRef.current

        if (pts.length >= 2) {
          completePath(pts, false)
        } else {
          resetDrawing(true)
        }

        // Do NOT stopPropagation — left-panel handles the tool switch.
        return
      }

      if (e.key === 'Escape') {
        const pts = pointsRef.current

        if (pts.length >= 2) {
          // Complete as an open path — no closing segment.
          // Do NOT stopPropagation so the LeftPanel Escape handler also fires and exits pen mode.
          completePath(pts, false)
        } else {
          // Not enough points — discard. LeftPanel Escape will deactivate the tool.
          resetDrawing(true)
        }
      }
    }

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceHeldRef.current = false
    }

    const onBlur = () => {
      spaceHeldRef.current = false
    }

    // Use capture so we run before other keydown listeners
    window.addEventListener('keydown', onKeyDown, { capture: true })
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true })
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
